import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Button, Table, Tag } from '@grovus/design-system'
import type { AdminCopy } from '../i18n'
import { decideRequest, fetchAdminRequests, type AdminRequest } from './api'
import { formatWhen, formatWhenShort } from './format'

type Lists =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ok'; pending: AdminRequest[]; decided: AdminRequest[] }

/**
 * A line under one row, held as page state keyed by request id rather than derived from
 * the lists: a request decided elsewhere long ago is in neither reloaded list, and its
 * outcome must still show (design, "Access requests").
 */
type Note = { kind: 'already'; request: AdminRequest } | { kind: 'identity' } | { kind: 'failed' } | { kind: 'uncertain' }

/**
 * The decision in progress. `sending` until the POST answers; `unsettled` when the answer
 * was lost or interrupted, until a current list read says whether it took effect.
 */
type Deciding = { id: string; phase: 'sending' | 'unsettled' }

export interface AccessRequests {
  lists: Lists
  /** Pending rows, plus rows decided elsewhere kept in place with their `already` note. */
  rows: AdminRequest[]
  notes: Record<string, Note>
  busy: string | null
  reload: () => void
  decide: (request: AdminRequest, decision: 'approve' | 'reject') => Promise<void>
}

/** The request queue's state. `onDenied` is the boundary's `leaveAdmin()`. */
export function useAccessRequests(onDenied: () => void): AccessRequests {
  const [lists, setLists] = useState<Lists>({ status: 'loading' })
  const [notes, setNotes] = useState<Record<string, Note>>({})
  const [kept, setKept] = useState<Record<string, AdminRequest>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const latest = useRef(0)
  const deciding = useRef<Deciding | null>(null)
  // A POST or a read can answer after the panel unmounted, and a read started then would
  // run under the next auth generation, where the generation check cannot reject it.
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  /**
   * Only the most recent load applies its lists: an older response would undo a later
   * decision. A denial applies from any load. A current read settles an unsettled
   * decision: it started after the POST answered, so a request no longer pending was
   * decided, and one still pending is not confirmed either way.
   */
  const reload = useCallback(async () => {
    const sequence = ++latest.current
    const result = await fetchAdminRequests()
    if (!alive.current) return
    if (result.status === 'denied') {
      onDenied()
      return
    }
    if (sequence !== latest.current) return
    const current = deciding.current
    if (result.status === 'ok') {
      setLists({ status: 'ok', ...result.body })
      if (current?.phase === 'unsettled') {
        const { id } = current
        const stillPending = result.body.pending.some((p) => p.id === id)
        deciding.current = null
        setNotes(({ [id]: _, ...rest }) => (stillPending ? { ...rest, [id]: { kind: 'failed' } } : rest))
        setKept(({ [id]: _, ...rest }) => rest)
        setBusy(null)
      }
      return
    }
    // While a decision is out the rows stay on screen, so its row keeps its lock and note.
    if (current?.phase === 'unsettled') {
      setNotes((notes) => ({ ...notes, [current.id]: { kind: 'uncertain' } }))
    } else if (!current && result.status !== 'stale' && result.status !== 'expired') {
      setLists({ status: 'failed' })
    }
  }, [onDenied])

  useEffect(() => { void reload() }, [reload])

  const decide = useCallback(async (request: AdminRequest, decision: 'approve' | 'reject') => {
    setBusy(request.id)
    deciding.current = { id: request.id, phase: 'sending' }
    // Kept in place, so a read that lists it as decided before the answer arrives does
    // not take away the row that carries its lock and note.
    setKept((current) => ({ ...current, [request.id]: request }))
    const result = await decideRequest(request.id, decision)
    if (!alive.current) return
    if (result.status === 'unavailable' || result.status === 'stale' || result.status === 'expired') {
      // The decision may have been committed: locked until a current read settles it.
      deciding.current = { id: request.id, phase: 'unsettled' }
      await reload()
      return
    }
    deciding.current = null
    setBusy(null)
    if (result.status === 'denied') {
      onDenied()
      return
    }
    if (result.status === 'conflict' && result.code === 'already_decided') {
      setNotes((current) => ({ ...current, [request.id]: { kind: 'already', request: result.request } }))
    } else {
      setNotes((current) => {
        const { [request.id]: _, ...rest } = current
        return result.status === 'conflict' ? { ...rest, [request.id]: { kind: 'identity' } } : rest
      })
      setKept(({ [request.id]: _, ...rest }) => rest)
    }
    await reload()
  }, [onDenied, reload])

  const pending = lists.status === 'ok' ? lists.pending : []
  const rows = [...pending, ...Object.values(kept).filter((k) => !pending.some((p) => p.id === k.id))]
    .sort((a, b) => a.requestedAt.localeCompare(b.requestedAt))

  return { lists, rows, notes, busy, reload: () => { void reload() }, decide }
}

interface Props {
  copy: AdminCopy
  narrow: boolean
  requests: AccessRequests
}

const BORDER = '1px solid var(--mantine-color-default-border)'
const FRAME: CSSProperties = { border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }
const ROW_BG = 'light-dark(var(--mantine-color-ink-0), var(--mantine-color-dark-6))'

function kindLabel(request: AdminRequest, copy: AdminCopy) {
  return request.kind === 'admission' ? copy.kindAdmission : copy.kindCreation
}

function Kind({ request, copy }: { request: AdminRequest; copy: AdminCopy }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
      <Tag>{kindLabel(request, copy)}</Tag>
      {request.memberships.map((m) => (
        <span key={m.companyName} className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{copy.memberIn(m.role, m.companyName)}</span>
      ))}
    </div>
  )
}

function confirmText(request: AdminRequest, copy: AdminCopy) {
  return request.kind === 'admission' ? copy.confirmRejectAdmission(request.email) : copy.confirmRejectCreation(request.email)
}

function noteText(note: Note, copy: AdminCopy) {
  if (note.kind === 'identity') return copy.identityConflict
  if (note.kind === 'failed') return copy.decisionFailed
  if (note.kind === 'uncertain') return copy.decisionUncertain
  const { status, decidedBy, decidedAt } = note.request
  return copy.alreadyDecided(status === 'approved' ? 'approved' : 'rejected', decidedBy ?? '', decidedAt ? formatWhenShort(decidedAt, copy) : '')
}

/** Screen 11: the pending queue oldest first, approval at once, an inline confirmation for a rejection, the last ten decisions. */
export function RequestsPage({ copy, narrow, requests }: Props) {
  const [confirm, setConfirm] = useState<{ id: string } | null>(null)
  const { lists, rows, notes, busy } = requests
  const pendingCount = lists.status === 'ok' ? lists.pending.length : 0

  function actions(request: AdminRequest): ReactNode {
    const note = notes[request.id]
    if (note?.kind === 'already') return null
    return (
      <>
        <Button variant="filled" size="xs" disabled={busy !== null} onClick={() => { setConfirm(null); void requests.decide(request, 'approve') }}>{copy.approve}</Button>
        <Button variant="default" size="xs" disabled={busy !== null} onClick={() => setConfirm({ id: request.id })}>{copy.reject}</Button>
      </>
    )
  }

  function confirmation(request: AdminRequest): ReactNode {
    if (confirm?.id !== request.id || notes[request.id]?.kind === 'already') return null
    const go = () => {
      setConfirm(null)
      void requests.decide(request, 'reject')
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 640 }}>
        <span style={{ fontSize: 'var(--mantine-font-size-xs)', textWrap: 'pretty', overflowWrap: 'anywhere' }}>{confirmText(request, copy)}</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button variant="default" size="compact-sm" c="bad.6" onClick={go}>{copy.reject}</Button>
          <Button variant="default" size="compact-sm" onClick={() => setConfirm(null)}>{copy.cancel}</Button>
        </div>
      </div>
    )
  }

  function note(request: AdminRequest): ReactNode {
    const n = notes[request.id]
    if (!n) return null
    const text = <span className="grv-muted" role="status" style={{ fontSize: 'var(--mantine-font-size-sm)', textWrap: 'pretty' }}>{noteText(n, copy)}</span>
    if (n.kind !== 'uncertain') return text
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        {text}
        <Button variant="default" size="compact-sm" onClick={requests.reload}>{copy.retry}</Button>
      </div>
    )
  }

  const decided = lists.status === 'ok' ? lists.decided : []

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h2 style={{ margin: 0, fontSize: 'var(--mantine-font-size-xl)', fontWeight: 700, letterSpacing: '-0.01em' }}>{copy.requestsTitle}</h2>
        <span className="grv-muted">{copy.requestsSub}</span>
      </div>

      {lists.status === 'failed' && (
        <div style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: 24 }}>
          <span className="grv-muted" role="alert">{copy.requestsFailed}</span>
          <Button variant="default" onClick={requests.reload}>{copy.retry}</Button>
        </div>
      )}

      {lists.status === 'ok' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span className="grv-label">{pendingCount ? `${copy.waiting} · ${pendingCount}` : copy.waiting}</span>

          {rows.length === 0 && (
            <div style={{ ...FRAME, padding: 24 }}>
              <span className="grv-muted" style={{ display: 'block', maxWidth: 520, textWrap: 'pretty' }}>{copy.empty}</span>
            </div>
          )}

          {rows.length > 0 && !narrow && (
            <div style={FRAME}>
              <Table>
                <colgroup><col style={{ width: 250 }} /><col style={{ width: 150 }} /><col style={{ width: 260 }} /><col /><col style={{ width: 180 }} /></colgroup>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th><span className="grv-label">{copy.email}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.requested}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.kind}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.message}</span></Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((request) => {
                    const open = confirm?.id === request.id
                    const extra = confirmation(request) ?? note(request)
                    return (
                      <Fragment key={request.id}>
                        <Table.Tr data-testid={`request-${request.id}`} bg={open ? ROW_BG : undefined} style={extra ? { borderBottom: 'none' } : undefined}>
                          <Table.Td><span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{request.email}</span></Table.Td>
                          <Table.Td><span className="grv-num grv-muted">{formatWhen(request.requestedAt, copy)}</span></Table.Td>
                          <Table.Td><Kind request={request} copy={copy} /></Table.Td>
                          <Table.Td><span style={{ textWrap: 'pretty' }}>{request.message}</span></Table.Td>
                          <Table.Td>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>{actions(request)}</div>
                          </Table.Td>
                        </Table.Tr>
                        {extra && (
                          <Table.Tr bg={open ? ROW_BG : undefined}>
                            <Table.Td colSpan={5} pt={0}>{extra}</Table.Td>
                          </Table.Tr>
                        )}
                      </Fragment>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </div>
          )}

          {rows.length > 0 && narrow && (
            <div style={{ ...FRAME, display: 'flex', flexDirection: 'column' }}>
              {rows.map((request, i) => (
                <div
                  key={request.id}
                  data-testid={`request-${request.id}`}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px', borderTop: i === 0 ? 'none' : BORDER, background: confirm?.id === request.id ? ROW_BG : undefined }}
                >
                  <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{request.email}</span>
                  <Kind request={request} copy={copy} />
                  <span className="grv-muted grv-num">{copy.requested}: {formatWhen(request.requestedAt, copy)}</span>
                  <span style={{ textWrap: 'pretty', overflowWrap: 'anywhere' }}>{request.message}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>{actions(request)}</div>
                  {confirmation(request) ?? note(request)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {lists.status === 'ok' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="decided">
          <span className="grv-label">{copy.decidedTitle}</span>
          {!narrow && decided.length > 0 && (
            <div style={FRAME}>
              <Table>
                <colgroup><col style={{ width: 250 }} /><col style={{ width: 280 }} /><col style={{ width: 130 }} /><col style={{ width: 210 }} /><col /></colgroup>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th><span className="grv-label">{copy.email}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.kind}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.decision}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.decidedBy}</span></Table.Th>
                    <Table.Th><span className="grv-label">{copy.when}</span></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {decided.map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td><span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{d.email}</span></Table.Td>
                      <Table.Td><Tag>{kindLabel(d, copy)}</Tag></Table.Td>
                      <Table.Td><span style={{ fontWeight: 500 }}>{d.status === 'approved' ? copy.approved : copy.rejected}</span></Table.Td>
                      <Table.Td><span className="grv-muted" style={{ overflowWrap: 'anywhere' }}>{d.decidedBy}</span></Table.Td>
                      <Table.Td><span className="grv-num grv-muted">{d.decidedAt ? formatWhen(d.decidedAt, copy) : ''}</span></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
          {narrow && decided.length > 0 && (
            <div style={{ ...FRAME, display: 'flex', flexDirection: 'column' }}>
              {decided.map((d, i) => (
                <div key={d.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px', borderTop: i === 0 ? 'none' : BORDER }}>
                  <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{d.email}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px' }}>
                    <Tag>{kindLabel(d, copy)}</Tag>
                    <span style={{ fontWeight: 500 }}>{d.status === 'approved' ? copy.approved : copy.rejected}</span>
                  </div>
                  <span className="grv-muted" style={{ overflowWrap: 'anywhere' }}>{copy.decidedBy}: {d.decidedBy}</span>
                  <span className="grv-muted grv-num">{d.decidedAt ? formatWhen(d.decidedAt, copy) : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
